'use server';
/**
 * Copy a STORY character (bookId != null — auto-created inside a generated story)
 * into the family's FIXED library (bookId NULL) as a normal, reusable character.
 * This is how a hero that turned out well in one story becomes a first-class
 * library character the parent can reuse everywhere.
 *
 * Mirrors seedCharactersFromTemplates: idempotent per (familyId, source char) via
 * a deterministic id, session is the source of truth (can't copy across families),
 * copies the full identity (descriptions + portrait) so the copy stands alone.
 */
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';

export async function copyCharacterFromStory(
  storyCharacterId: string,
): Promise<ActionResult<{ characterId: string }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const creatorId = await SessionService.getUserId();
    if (!familyId || !creatorId) return { success: false, error: 'Not authenticated' };

    const { getCharacterRepository } = await import('@/contexts/characters/repositories');
    const repo = getCharacterRepository();

    const src = await repo.getById(storyCharacterId);
    // Ownership: only a character in the caller's own family can be copied.
    if (!src || src.familyId !== familyId) return { success: false, error: 'Character not found' };

    // Idempotent: re-copying the same source returns the existing library copy.
    const copyId = `copy_${familyId}_${storyCharacterId}`;
    const existing = await repo.getById(copyId);
    if (existing) return { success: true, data: { characterId: existing.id } };

    const created = await repo.create({
      id: copyId,
      familyId,
      creatorId,
      name: src.name,
      role: src.role ?? undefined,
      gender: src.gender ?? undefined,
      age: src.age ?? undefined,
      creationMethod: 'fromStory',
      physicalDescription: src.physicalDescription ?? undefined,
      personalityTraits: src.personalityTraits ?? undefined,
      specialAbilities: src.specialAbilities ?? undefined,
      visualDescription: src.visualDescription ?? undefined,
      synonyms: src.synonyms ?? [],
      // Carry the portrait so the library copy looks identical + stays the
      // consistency anchor (bookId omitted → NULL → a library character).
      primaryImageUrl: src.primaryImageUrl ?? undefined,
      primaryPortraitUrl: src.primaryPortraitUrl ?? undefined,
      faceReferenceUrl: src.faceReferenceUrl ?? undefined,
      // Carry living/inanimate so a promoted object (turnip) keeps rendering
      // face-less across every story it's reused in.
      living: src.living,
    });

    return { success: true, data: { characterId: created.id } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('copyCharacterFromStory', e) };
  }
}
